/**
 * Pure, schema-aware diff of two ProseMirror {@link Slice}s into a
 * list of reviewable {@link SliceSegment}s.
 *
 * The Post Stream Review (PSR) toolbar lets the author review an AI change one
 * segment at a time. A single AI tool-call commit is stored as ONE coarse
 * `AIContentPositionEntry` (an `originalUserContentSlice` → `newSlice` pair over
 * a single range). This helper splits that coarse pair into FINE per-top-level
 * -node (and per-list-item) segments so each node the AI touched can be
 * navigated, diffed and undone/redone independently.
 *
 * It is intentionally **pure**: no ProseMirror transactions, no absolute
 * document positions, no React. It works purely on the two slices' content
 * `Fragment`s and returns fragment-relative offsets. Turning those offsets into
 * absolute live-document positions is the job of `segmentCommitPositions.ts`,
 * which runs inside the PSR against the live `EditorView`.
 *
 * ── Algorithm ────────────────────────────────────────────────────────────────
 *   1. Treat each slice's content as a sequence of top-level nodes.
 *   2. Key each node by a stable signature (`type.name` + stable-stringified
 *      `toJSON()`), so structurally-identical nodes match regardless of object
 *      identity or key ordering.
 *   3. Run an LCS diff over the two signature sequences. Matching nodes are
 *      anchors (unchanged → no segment emitted).
 *   4. Each maximal run of non-matching nodes between anchors becomes
 *      segment(s): only-new → `add`, only-original → `remove`, both → `update`
 *      (paired 1:1, remainder folds into a trailing add/remove).
 *   5. List handling: when an aligned `update` pair is a single list node of the
 *      SAME list type on both sides, recurse one level over its item children so
 *      each changed list item becomes its own segment (re-wrapped in a
 *      single-item list of the same type to stay schema-valid).
 *
 * The function is symmetric: an empty `newSlice` yields all-`remove`, an empty
 * `originalSlice` yields all-`add`. No change yields `[]`.
 */
import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { SegmentKind } from '../../../../pm-plugins/ai-session-context/types';

export interface SliceSegment {
	kind: SegmentKind;
	/** Offset (within `newSlice.content`) where this segment's new content starts. */
	newFrom: number;
	/** The new content for this segment (open depth 0; top-level nodes). */
	newSlice: Slice;
	/** Offset (within `newSlice.content`) where this segment's new content ends. */
	newTo: number;
	/** Offset (within `originalSlice.content`) where this segment's original content starts. */
	originalFrom: number;
	/** The original content for this segment (open depth 0; top-level nodes). */
	originalSlice: Slice;
	/** Offset (within `originalSlice.content`) where this segment's original content ends. */
	originalTo: number;
}

/**
 * Deterministically stringify a JSON value so two structurally-equal nodes
 * produce the same key regardless of property insertion order.
 */
const stableStringify = (value: unknown): string => {
	if (value === null || typeof value !== 'object') {
		return JSON.stringify(value);
	}
	if (Array.isArray(value)) {
		return `[${value.map(stableStringify).join(',')}]`;
	}
	const keys = Object.keys(value as Record<string, unknown>).sort();
	return `{${keys
		.map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`)
		.join(',')}}`;
};

/**
 * Above this `nodeSize` a node is keyed by a cheap structural
 * fingerprint instead of a full JSON serialisation. `nodeSignature` is only
 * used as an LCS anchor key; large nodes that aren't byte-identical rarely
 * survive as anchors anyway, so this avoids an `O(subtree size)` stringify per
 * node on big AI commits (e.g. a full-page rewrite) while keeping equal nodes
 * matching: two large nodes collide only if type, size AND text all match, in
 * which case treating them as an anchor (unchanged) is acceptable.
 */
const LARGE_NODE_SIGNATURE_THRESHOLD = 2000;

/** Stable signature for a single node: type name + stable-stringified JSON. */
const nodeSignature = (node: PMNode): string =>
	node.nodeSize > LARGE_NODE_SIGNATURE_THRESHOLD
		? `${node.type.name}#${node.nodeSize}#${node.textContent.length}`
		: `${node.type.name}:${stableStringify(node.toJSON())}`;

/** Collect the top-level children of a fragment into an array. */
const childrenOf = (fragment: Fragment): PMNode[] => {
	const out: PMNode[] = [];
	fragment.forEach((child) => {
		out.push(child);
	});
	return out;
};

/** Sum of nodeSize for a list of nodes (fragment-relative span). */
const span = (nodes: PMNode[]): number => nodes.reduce((acc, n) => acc + n.nodeSize, 0);

/** Wrap a list of top-level nodes into an open-depth-0 Slice. */
const sliceOf = (nodes: PMNode[]): Slice =>
	nodes.length === 0 ? Slice.empty : new Slice(Fragment.fromArray(nodes), 0, 0);

const LIST_TYPES = new Set(['bulletList', 'orderedList']);
const LIST_ITEM_TYPES = new Set(['listItem']);

/**
 * Normalise an asymmetric "bare list-item run vs. single list node" pair so the
 * per-item recursion in `emitListItemSegments` can fire.
 *
 * When one side's top level is a single list and the other's is a bare run of
 * `listItem`s (no enclosing list), the top-level LCS diff aligns `listItem`
 * against the list node (different types) and emits one coarse `update` instead
 * of a segment per item. We wrap the bare run into a list of the other side's
 * type so the diff aligns list ↔ list. The wrap is purely structural — items
 * are re-wrapped as single-item lists downstream — so live positions (driven by
 * `newSlice`) and per-segment reconstruction are unchanged.
 *
 * Returns the (possibly rewritten) `[originalSlice, newSlice]`; unchanged when
 * the input isn't this shape.
 */
const isBareListItemRun = (nodes: PMNode[]): boolean =>
	nodes.length > 0 && nodes.every((n) => LIST_ITEM_TYPES.has(n.type.name));

const isSingleListNode = (nodes: PMNode[]): boolean =>
	nodes.length === 1 && LIST_TYPES.has(nodes[0].type.name);

/**
 * `taskList`/`taskItem` are handled separately from the generic
 * `bulletList`/`orderedList` path: a `taskList` can only contain `taskItem`s
 * (and a `taskItem` run can only be re-wrapped in a `taskList`), so wrapping a
 * `taskItem` run in a generic list — or `listItem`s in a `taskList` — would
 * produce a schema-invalid node. That invalid node would flow into a segment's
 * `originalSlice` and corrupt the document when an undo replays it via
 * `tr.replaceRange`. Keeping the two families apart means each wrap is always
 * schema-valid by construction.
 */
const isBareTaskItemRun = (nodes: PMNode[]): boolean =>
	nodes.length > 0 && nodes.every((n) => n.type.name === 'taskItem');

const isSingleTaskList = (nodes: PMNode[]): boolean =>
	nodes.length === 1 && nodes[0].type.name === 'taskList';

const wrapInList = (list: PMNode, items: PMNode[]): Slice =>
	new Slice(Fragment.from(list.type.create(list.attrs, Fragment.fromArray(items))), 0, 0);

const normalizeListItemRunAgainstList = (originalSlice: Slice, newSlice: Slice): [Slice, Slice] => {
	const originalNodes = childrenOf(originalSlice.content);
	const newNodes = childrenOf(newSlice.content);

	// Wrap the bare list-item run into a list matching the other side's type.
	// `bulletList`/`orderedList` ↔ bare `listItem` run.
	if (isSingleListNode(newNodes) && isBareListItemRun(originalNodes)) {
		return [wrapInList(newNodes[0], originalNodes), newSlice];
	}
	if (isSingleListNode(originalNodes) && isBareListItemRun(newNodes)) {
		return [originalSlice, wrapInList(originalNodes[0], newNodes)];
	}

	// `taskList` ↔ bare `taskItem` run. Wrap a bare `taskItem` run in a
	// `taskList` only when the other side is a `taskList`, so the wrapped node is
	// always schema-valid (a `taskList` may only contain `taskItem`s).
	if (isSingleTaskList(newNodes) && isBareTaskItemRun(originalNodes)) {
		return [wrapInList(newNodes[0], originalNodes), newSlice];
	}
	if (isSingleTaskList(originalNodes) && isBareTaskItemRun(newNodes)) {
		return [originalSlice, wrapInList(originalNodes[0], newNodes)];
	}

	return [originalSlice, newSlice];
};

/**
 * Classic LCS over two signature arrays. Returns matched index pairs
 * `[i, j]` (i into `a`, j into `b`) in ascending order.
 */
const lcsPairs = (a: string[], b: string[]): Array<[number, number]> => {
	const n = a.length;
	const m = b.length;
	const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
		}
	}
	const pairs: Array<[number, number]> = [];
	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		if (a[i] === b[j]) {
			pairs.push([i, j]);
			i++;
			j++;
		} else if (dp[i + 1][j] >= dp[i][j + 1]) {
			i++;
		} else {
			j++;
		}
	}
	return pairs;
};

export function segmentSlice(originalSliceInput: Slice, newSliceInput: Slice): SliceSegment[] {
	// Reconcile the "bare list-item run vs. list node" shape so per-item
	// recursion can fire (see helper docs).
	const [originalSlice, newSlice] = normalizeListItemRunAgainstList(
		originalSliceInput,
		newSliceInput,
	);

	const originalNodes = childrenOf(originalSlice.content);
	const newNodes = childrenOf(newSlice.content);

	// Running fragment-relative offsets into each side's content.
	const segments: SliceSegment[] = [];

	const originalSigs = originalNodes.map(nodeSignature);
	const newSigs = newNodes.map(nodeSignature);
	const anchors = lcsPairs(originalSigs, newSigs);

	// Precompute cumulative offsets so we can map a node index → fragment offset.
	const originalOffsets: number[] = [0];
	for (const n of originalNodes) {
		originalOffsets.push(originalOffsets[originalOffsets.length - 1] + n.nodeSize);
	}
	const newOffsets: number[] = [0];
	for (const n of newNodes) {
		newOffsets.push(newOffsets[newOffsets.length - 1] + n.nodeSize);
	}

	// Walk the gaps between consecutive anchors (treating doc start/end as
	// implicit anchors at the extremes).
	let oi = 0; // next unconsumed original index
	let nj = 0; // next unconsumed new index

	const emitGap = (oStart: number, oEnd: number, nStart: number, nEnd: number) => {
		const oGap = originalNodes.slice(oStart, oEnd);
		const nGap = newNodes.slice(nStart, nEnd);

		if (oGap.length === 0 && nGap.length === 0) {
			return;
		}

		// Pure add.
		if (oGap.length === 0) {
			segments.push({
				kind: 'add',
				originalFrom: originalOffsets[oStart],
				originalTo: originalOffsets[oStart],
				newFrom: newOffsets[nStart],
				newTo: newOffsets[nEnd],
				originalSlice: Slice.empty,
				newSlice: sliceOf(nGap),
			});
			return;
		}

		// Pure remove.
		if (nGap.length === 0) {
			segments.push({
				kind: 'remove',
				originalFrom: originalOffsets[oStart],
				originalTo: originalOffsets[oEnd],
				newFrom: newOffsets[nStart],
				newTo: newOffsets[nStart],
				originalSlice: sliceOf(oGap),
				newSlice: Slice.empty,
			});
			return;
		}

		// Mixed gap → pair 1:1 as updates; fold remainder into a trailing
		// add/remove.
		const pairCount = Math.min(oGap.length, nGap.length);
		for (let k = 0; k < pairCount; k++) {
			const oNode = oGap[k];
			const nNode = nGap[k];
			const oFrom = originalOffsets[oStart + k];
			const nFrom = newOffsets[nStart + k];

			// List recursion: same list type on both sides → split per item.
			if (
				oNode.type === nNode.type &&
				LIST_TYPES.has(oNode.type.name) &&
				emitListItemSegments(oNode, nNode, oFrom + 1, nFrom + 1, segments)
			) {
				continue;
			}

			segments.push({
				kind: 'update',
				originalFrom: oFrom,
				originalTo: oFrom + oNode.nodeSize,
				newFrom: nFrom,
				newTo: nFrom + nNode.nodeSize,
				originalSlice: sliceOf([oNode]),
				newSlice: sliceOf([nNode]),
			});
		}

		// Trailing remainder.
		if (oGap.length > pairCount) {
			const rest = oGap.slice(pairCount);
			segments.push({
				kind: 'remove',
				originalFrom: originalOffsets[oStart + pairCount],
				originalTo: originalOffsets[oEnd],
				newFrom: newOffsets[nEnd],
				newTo: newOffsets[nEnd],
				originalSlice: sliceOf(rest),
				newSlice: Slice.empty,
			});
		} else if (nGap.length > pairCount) {
			const rest = nGap.slice(pairCount);
			segments.push({
				kind: 'add',
				originalFrom: originalOffsets[oEnd],
				originalTo: originalOffsets[oEnd],
				newFrom: newOffsets[nStart + pairCount],
				newTo: newOffsets[nEnd],
				originalSlice: Slice.empty,
				newSlice: sliceOf(rest),
			});
		}
	};

	for (const [ai, bj] of anchors) {
		emitGap(oi, ai, nj, bj);
		oi = ai + 1;
		nj = bj + 1;
	}
	// Trailing gap after the last anchor.
	emitGap(oi, originalNodes.length, nj, newNodes.length);

	return segments;
}

/**
 * Recurse one level into two same-typed list nodes, emitting one segment per
 * changed list item (re-wrapped as a single-item list of the same type so
 * `replaceRange` stays schema-valid). Returns `true` if it handled the pair
 * (always, when the list types match); the caller then skips the generic
 * `update` segment for this pair.
 *
 * `oBase` / `nBase` are the fragment-relative offsets of the FIRST CHILD of each
 * list (i.e. the list's own offset + 1 for entering the list node).
 */
function emitListItemSegments(
	oList: PMNode,
	nList: PMNode,
	oBase: number,
	nBase: number,
	segments: SliceSegment[],
): boolean {
	const oItems = childrenOf(oList.content);
	const nItems = childrenOf(nList.content);

	const oSigs = oItems.map(nodeSignature);
	const nSigs = nItems.map(nodeSignature);
	const anchors = lcsPairs(oSigs, nSigs);

	const oOffsets: number[] = [oBase];
	for (const it of oItems) {
		oOffsets.push(oOffsets[oOffsets.length - 1] + it.nodeSize);
	}
	const nOffsets: number[] = [nBase];
	for (const it of nItems) {
		nOffsets.push(nOffsets[nOffsets.length - 1] + it.nodeSize);
	}

	// Wrap items back into a single-item list of the original list's type.
	const wrap = (list: PMNode, items: PMNode[]): Slice =>
		items.length === 0
			? Slice.empty
			: new Slice(Fragment.from(list.type.create(list.attrs, Fragment.fromArray(items))), 0, 0);

	let oi = 0;
	let nj = 0;

	const emitItemGap = (oStart: number, oEnd: number, nStart: number, nEnd: number) => {
		const oGap = oItems.slice(oStart, oEnd);
		const nGap = nItems.slice(nStart, nEnd);
		if (oGap.length === 0 && nGap.length === 0) {
			return;
		}
		if (oGap.length === 0) {
			segments.push({
				kind: 'add',
				originalFrom: oOffsets[oStart],
				originalTo: oOffsets[oStart],
				newFrom: nOffsets[nStart],
				newTo: nOffsets[nEnd],
				originalSlice: Slice.empty,
				newSlice: wrap(nList, nGap),
			});
			return;
		}
		if (nGap.length === 0) {
			segments.push({
				kind: 'remove',
				originalFrom: oOffsets[oStart],
				originalTo: oOffsets[oEnd],
				newFrom: nOffsets[nStart],
				newTo: nOffsets[nStart],
				originalSlice: wrap(oList, oGap),
				newSlice: Slice.empty,
			});
			return;
		}
		const pairCount = Math.min(oGap.length, nGap.length);
		for (let k = 0; k < pairCount; k++) {
			segments.push({
				kind: 'update',
				originalFrom: oOffsets[oStart + k],
				originalTo: oOffsets[oStart + k] + oGap[k].nodeSize,
				newFrom: nOffsets[nStart + k],
				newTo: nOffsets[nStart + k] + nGap[k].nodeSize,
				originalSlice: wrap(oList, [oGap[k]]),
				newSlice: wrap(nList, [nGap[k]]),
			});
		}
		if (oGap.length > pairCount) {
			segments.push({
				kind: 'remove',
				originalFrom: oOffsets[oStart + pairCount],
				originalTo: oOffsets[oEnd],
				newFrom: nOffsets[nEnd],
				newTo: nOffsets[nEnd],
				originalSlice: wrap(oList, oGap.slice(pairCount)),
				newSlice: Slice.empty,
			});
		} else if (nGap.length > pairCount) {
			segments.push({
				kind: 'add',
				originalFrom: oOffsets[oEnd],
				originalTo: oOffsets[oEnd],
				newFrom: nOffsets[nStart + pairCount],
				newTo: nOffsets[nEnd],
				originalSlice: Slice.empty,
				newSlice: wrap(nList, nGap.slice(pairCount)),
			});
		}
	};

	for (const [ai, bj] of anchors) {
		emitItemGap(oi, ai, nj, bj);
		oi = ai + 1;
		nj = bj + 1;
	}
	emitItemGap(oi, oItems.length, nj, nItems.length);

	return true;
}

// Re-export for tests that want the internal span helper.
// Explicitly typed so declaration emission works under --isolatedDeclarations
// (shorthand properties can't be inferred otherwise).
export const __testables: {
	lcsPairs: (a: string[], b: string[]) => Array<[number, number]>;
	nodeSignature: (node: PMNode) => string;
	span: (nodes: PMNode[]) => number;
} = { span, nodeSignature, lcsPairs };
